export interface Article {
  id: string;
  title: string;
  slug: string;
  categoryId: string;
  categoryName: string;
  excerpt: string;
  content: string;
  coverImage: string;
  author: string;
  publishDate: string;
  readTime: string;
  featured: boolean;
}

export const articles: Article[] = [
  {
    id: 'art-1',
    title: '青春期身体变化：你需要知道的科学知识',
    slug: 'puberty-body-changes',
    categoryId: 'cat-1',
    categoryName: '青春期知识',
    excerpt: '青春期是每个人成长的必经阶段。了解身体在青春期发生的变化，掌握科学的生理知识，有助于青少年从容面对成长中的各种挑战。本文系统讲解青春期生理发育的规律与注意事项。',
    content: `<p>青春期是人生中最重要的发育阶段之一，通常在 10-19 岁之间。在这个阶段，青少年的身体会发生一系列显著的变化，这些变化虽然各有不同，但都是正常且健康的发育过程。</p>
<h3>身体发育的基本规律</h3>
<p>每个人的发育节奏都不尽相同，有些青少年可能在 10 岁就开始进入青春期，而有些人可能到 14 岁才开始。这种差异完全正常，不需要过度担心。</p>
<p>在青春期，大脑中的下丘脑会向垂体发送信号，促使身体开始分泌性激素。对于男孩来说，主要是睾酮；对于女孩来说，主要是雌激素和孕激素。这些激素会引发一系列的生理变化。</p>
<h3>共同的生理变化</h3>
<p>无论男女，青春期都会经历以下共同变化：身高快速增长、体重增加、皮肤油脂分泌增多（可能导致青春痘）、体毛生长、汗腺活跃等。这些都是成长的正常标志。</p>
<h3>如何应对青春期的困惑</h3>
<p>面对身体的变化，感到困惑或不安是非常正常的。重要的是要记住：你并不孤单，每个人的成长道路都是独特的。如果你有任何疑问，可以向父母、老师或学校的心理咨询师寻求帮助。</p>
<p>科学的性教育能够帮助青少年建立正确的身体认知，消除不必要的焦虑，培养积极健康的自我形象。记住，了解自己的身体是爱自己的第一步。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Warm%20illustration%20of%20teenagers%20reading%20educational%20books%20together%20in%20a%20bright%20library%20with%20natural%20light%2C%20gentle%20color%20palette%2C%20supportive%20learning%20environment%2C%20editorial%20photography%20style%20with%20soft%20warm%20tones&width=800&height=500&seq=article-puberty-01&orientation=landscape',
    author: '李教授',
    publishDate: '2024-12-15',
    readTime: '8 分钟',
    featured: true,
  },
  {
    id: 'art-2',
    title: '建立身体边界：青少年自我保护实用指南',
    slug: 'establishing-body-boundaries',
    categoryId: 'cat-2',
    categoryName: '自我保护',
    excerpt: '学会建立和维护个人边界是青少年自我保护的重要能力。本文介绍如何识别不安全的接触、勇敢说"不"、以及在遇到危险时如何有效求助，帮助每一位青少年守护自己的安全。',
    content: `<p>自我保护是每个青少年都应该掌握的重要技能。建立清晰的身体边界，不仅能够保护自己免受伤害，也是培养自尊和自信的重要一步。</p>
<h3>什么是身体边界</h3>
<p>身体边界是指每个人对自己身体的控制权和决定权。简单来说，你的身体属于你自己，你有权决定谁可以触碰你、以什么方式触碰你。没有任何人有权利未经你的同意触碰你的身体。</p>
<h3>识别不安全的信号</h3>
<p>学会识别潜在的危睑信号非常重要。如果有人让你感到不舒服、试图强迫你做不愿意做的事情、或者在私下场合对你做出不恰当的举动，这些都是需要警惕的信号。相信你的直觉——如果某个情境让你感到不安，那很可能确实存在问题。</p>
<h3>学会说"不"</h3>
<p>表达拒绝是每个人的权利。你可以用清晰、坚定的语气说"不"，不需要为此感到抱歉或内疚。练习在不同情境下表达拒绝的方式，能够帮助你在真正需要的时候更加自信地说出来。</p>
<h3>寻求帮助的渠道</h3>
<p>如果你遇到了困扰或危险，不要独自承受。以下资源可以帮助你：</p><ul><li>向父母或信任的成年人倾诉</li><li>联系学校的心理咨询老师</li><li>拨打 12355 青少年服务热线</li><li>拨打 110 报警电话（紧急情况）</li></ul>
<p>记住：遇到问题不是你的错，寻求帮助是勇敢的表现。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=A%20teenager%20standing%20confidently%20with%20arms%20crossed%20in%20a%20protective%20gesture%2C%20warm%20soft%20lighting%2C%20minimalist%20background%20with%20warm%20beige%20tones%2C%20empowering%20atmosphere%2C%20editorial%20photography%20style&width=800&height=500&seq=article-protection-01&orientation=landscape',
    author: '陈老师',
    publishDate: '2024-11-28',
    readTime: '10 分钟',
    featured: true,
  },
  {
    id: 'art-3',
    title: '青春期的情绪波动：理解与管理你的感受',
    slug: 'teenage-emotional-management',
    categoryId: 'cat-3',
    categoryName: '心理健康',
    excerpt: '青春期情绪起伏大是正常的生理和心理现象。本文帮你理解情绪波动背后的原因，提供实用的情绪管理技巧，帮助青少年更好地与自己的感受相处，培养健康的心理状态。',
    content: `<p>如果最近你发现自己一会儿开心、一会儿难过、一会儿又莫名地烦躁——请不要担心，这在青春期是非常正常的现象。你的大脑正在经历重要的发育和重组，这会影响你的情绪调节能力。</p>
<h3>情绪波动的原因</h3>
<p>青春期的情绪波动主要由两个因素造成：一是激素水平的剧烈变化，二是大脑前额叶皮层（负责理性思考和情绪控制的区域）还在发育中。这就像一辆装了强劲引擎但刹车系统还在调试中的车——动力十足但有时候不太好控制。</p>
<h3>实用的情绪管理技巧</h3>
<p>以下是一些经过验证有效的情绪管理方法：深呼吸练习、写情绪日记、运动释放、找人倾诉、音乐放松等。找到适合自己的方式很重要，因为每个人的感受和应对方式都不同。</p>
<h3>何时需要寻求专业帮助</h3>
<p>虽然情绪波动是正常的，但如果出现以下情况，建议寻求专业心理咨询师的帮助：持续两周以上的情绪低落、对以前喜欢的事情完全失去兴趣、睡眠或食欲发生显著变化、有自我伤害的想法。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=A%20teenager%20journaling%20peacefully%20by%20a%20sunlit%20window%20with%20plants%2C%20soft%20warm%20light%2C%20calm%20and%20reflective%20atmosphere%2C%20minimalist%20setting%2C%20editorial%20photography%20with%20gentle%20warm%20tones&width=800&height=500&seq=article-mental-01&orientation=landscape',
    author: '王博士',
    publishDate: '2024-11-10',
    readTime: '7 分钟',
    featured: true,
  },
  {
    id: 'art-4',
    title: '如何与孩子谈性：家长沟通指南',
    slug: 'parent-child-sex-education-talk',
    categoryId: 'cat-4',
    categoryName: '家庭教育',
    excerpt: '很多家长在面对性教育话题时感到不知所措。本文为家长提供科学、实用的沟通方法和技巧，帮助您以自然、坦诚的方式与孩子讨论性教育，建立良好的亲子沟通关系。',
    content: `<p>作为家长，与孩子谈论性教育可能是最具挑战性但也最重要的家庭教育内容之一。很多父母担心"说得太多"或"说得太早"，但实际上，适时、适当的性教育对孩子的健康成长至关重要。</p>
<h3>为什么要主动交流</h3>
<p>在信息爆炸的时代，孩子获取信息的渠道比我们想象的要多得多。与其让孩子从不可靠的渠道获取不完整甚至错误的信息，不如由您——他们最信任的人——来提供准确、健康的性教育。研究表明，接受过良好家庭性教育的青少年更可能在性行为上做出负责任的决定。</p>
<h3>不同年龄段的沟通策略</h3>
<p>性教育是一个持续的过程，而非一次性谈话。不同年龄段的孩子需要不同深度和角度的信息。关键原则是：回答孩子提出的问题，但不需要一次性给出所有信息。从幼儿园阶段的身体认知，到小学阶段的青春期准备，再到中学阶段的深度话题，循序渐进。</p>
<h3>创造安全的沟通环境</h3>
<p>让孩子知道他们可以随时来找您讨论任何问题，而不会受到批评或惩罚。保持冷静、开放的态度，使用正确的术语而非代称，这些都传递着"这个话题是可以正常讨论的"这一重要信号。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=A%20warm%20illustration%20of%20a%20parent%20and%20teenager%20sitting%20together%20on%20a%20cozy%20sofa%20talking%20gently%2C%20soft%20natural%20lighting%20from%20window%2C%20loving%20family%20atmosphere%2C%20editorial%20photography%20style&width=800&height=500&seq=article-family-01&orientation=landscape',
    author: '李教授',
    publishDate: '2024-10-22',
    readTime: '12 分钟',
    featured: false,
  },
  {
    id: 'art-5',
    title: '打破性别刻板印象：每个孩子都可以做自己',
    slug: 'breaking-gender-stereotypes',
    categoryId: 'cat-5',
    categoryName: '性别平等',
    excerpt: '"男孩应该坚强""女孩应该温柔"——这些性别刻板印象限制着每个孩子的自由发展。本文探讨如何识别和打破性别刻板印象，帮助青少年在尊重多元的环境中成长为真正的自己。',
    content: `<p>从玩具的颜色到职业的选择，性别刻板印象几乎渗透在我们生活的方方面面。这些看似无害的观念，实际上可能严重限制每个孩子的自由发展和自我实现。</p>
<h3>性别刻板印象从哪里来</h3>
<p>性别刻板印象是社会文化长期形成的产物。从家庭、学校到媒体，无处不在的性别角色期待潜移默化地影响着青少年的自我认知和行为选择。研究表明，儿童在 3 岁左右就开始形成性别角色意识。</p>
<h3>刻板印象的危害</h3>
<p>性别刻板印象会限制个人的兴趣发展、职业选择、情感表达和人际关系。"男孩不能哭"的观念导致许多男性压抑情感；"女孩不适合学理科"的偏见让无数有天赋的女孩放弃了对科学的热爱。</p>
<h3>如何培养性别平等意识</h3>
<p>培养性别平等意识需要从日常生活做起：鼓励孩子尝试各种类型的活动和玩具、使用性别中立的语言、展示多元的成功榜样、及时纠正孩子的性别偏见言论。最重要的是，让孩子知道：无论你是谁，你的价值不由性别决定。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Diverse%20group%20of%20teenagers%20collaborating%20and%20laughing%20together%20in%20a%20bright%20modern%20maker%20space%2C%20equal%20participation%2C%20inclusive%20atmosphere%2C%20warm%20natural%20lighting%2C%20editorial%20photography%20style&width=800&height=500&seq=article-gender-01&orientation=landscape',
    author: '陈老师',
    publishDate: '2024-10-05',
    readTime: '9 分钟',
    featured: false,
  },
  {
    id: 'art-6',
    title: '网络时代的自我保护：青少年网络安全指南',
    slug: 'online-safety-for-teens',
    categoryId: 'cat-2',
    categoryName: '自我保护',
    excerpt: '在数字时代，网络安全已成为青少年自我保护的重要组成部分。本文教你如何识别网络风险、保护个人隐私、应对网络骚扰，安全自信地畅游数字世界。',
    content: `<p>互联网为青少年提供了无限的学习和社交机会，但同时也带来了新的安全风险。掌握网络安全知识，已经成为数字时代青少年必备的自我保护技能。</p>
<h3>常见的网络风险</h3>
<p>青少年在网络上面临的主要风险包括：网络骚扰和欺凌、隐私泄露、不当内容接触、网络诱骗、过度使用和成瘾等。了解这些风险是保护自己的第一步。</p>
<h3>保护个人隐私的实用方法</h3>
<p>不要在社交媒体上公开分享个人敏感信息（家庭住址、学校名称、电话号码等）；使用强密码并定期更换；谨慎添加陌生人为好友；开启社交媒体的隐私设置；定期检查自己在网络上的数字足迹。</p>
<h3>遇到问题怎么办</h3>
<p>如果在网络上遇到骚扰、威胁或任何让你不舒服的情况：保存证据（截图）、立即停止与对方交流、向平台举报、告知信任的成年人、必要时报警处理。记住：这不是你的错，你有权在网络空间中获得安全和尊重。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=A%20teenager%20using%20a%20laptop%20in%20a%20bright%20cozy%20room%20with%20plants%2C%20digital%20safety%20concept%2C%20warm%20soft%20lighting%2C%20modern%20minimalist%20interior%2C%20editorial%20photography%20style&width=800&height=500&seq=article-online-01&orientation=landscape',
    author: '王博士',
    publishDate: '2024-09-18',
    readTime: '11 分钟',
    featured: false,
  },
  {
    id: 'art-7',
    title: '月经那些事：女孩青春期的必修课',
    slug: 'all-about-menstruation',
    categoryId: 'cat-1',
    categoryName: '青春期知识',
    excerpt: '月经是女性正常的生理现象，但很多女孩对此缺乏正确的了解。本文全面介绍月经的科学知识、经期护理方法，帮助女孩们从容面对青春期的重要变化。',
    content: `<p>月经是女孩进入青春期的重要标志之一，通常发生在 10-16 岁之间。虽然初次来潮可能会让人感到紧张和困惑，但了解相关科学知识能够帮助你从容面对这一自然的生理过程。</p>
<h3>月经的科学原理</h3>
<p>月经是子宫内膜周期性脱落的结果。每个月，卵巢会释放一个卵子（排卵），同时子宫内膜会增厚为可能的怀孕做准备。如果卵子没有受精，增厚的子宫内膜就会脱落，形成月经。</p>
<h3>经期护理要点</h3>
<p>保持个人卫生：勤换卫生用品（卫生巾每 3-4 小时更换一次）；温水清洗外阴，避免使用刺激性清洁产品；注意保暖，避免受凉；合理饮食，适当补充铁质；适度运动有助于缓解经期不适。</p>
<h3>常见问题解答</h3>
<p>月经周期不规律在初潮后的前两年非常正常；轻度腹痛可以通过热敷、温和运动和休息缓解；如果出现严重疼痛或异常出血，应及时就医咨询。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Soft%20gentle%20illustration%20of%20a%20teenage%20girl%20reading%20a%20health%20book%20in%20a%20cozy%20sunlit%20corner%20with%20warm%20blankets%2C%20calm%20and%20caring%20atmosphere%2C%20soft%20pastel%20warm%20tones%2C%20editorial%20photography%20style&width=800&height=500&seq=article-menstruation-01&orientation=landscape',
    author: '张医生',
    publishDate: '2024-09-05',
    readTime: '8 分钟',
    featured: false,
  },
  {
    id: 'art-8',
    title: '尊重与同意：健康人际关系的基础',
    slug: 'respect-and-consent',
    categoryId: 'cat-5',
    categoryName: '性别平等',
    excerpt: '"同意"是所有人际交往中最基本的原则。本文帮助青少年理解同意的真正含义，学会在人际交往中尊重他人也保护自己，建立健康平等的人际关系。',
    content: `<p>在所有的人际关系中，"同意"（Consent）是最基础也最重要的原则。理解并践行同意文化，不仅关乎性教育，更是尊重他人、保护自己的重要人生技能。</p>
<h3>什么是真正的同意</h3>
<p>真正的同意具有以下特征：自愿的（非强迫或威胁）、明确的（清晰的"是"，而非"不拒绝"）、知情的（了解将要发生的事情）、可撤销的（随时可以改变主意）。沉默、穿着、之前的行为都不等于同意。</p>
<h3>日常生活中的同意</h3>
<p>同意原则不仅适用于亲密关系，也适用于日常交往：借用他人物品前先询问、分享他人照片前征得许可、尊重朋友说"不想去"的决定。从小事做起，培养尊重他人意愿的习惯。</p>
<h3>如何表达和接受拒绝</h3>
<p>学会清晰表达自己的边界，同时尊重他人的边界。当别人说"不"时，接受而非追问或施压。被拒绝不是对你的否定，而是对他人自主权的尊重。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Two%20teenagers%20having%20a%20respectful%20conversation%20in%20a%20bright%20school%20courtyard%2C%20warm%20sunlight%2C%20friendly%20and%20respectful%20body%20language%2C%20modern%20educational%20setting%2C%20editorial%20photography%20style&width=800&height=500&seq=article-consent-01&orientation=landscape',
    author: '陈老师',
    publishDate: '2024-08-20',
    readTime: '10 分钟',
    featured: true,
  },
];

export const getFeaturedArticles = (): Article[] => {
  return articles.filter((article) => article.featured);
};

export const getArticlesByCategory = (categoryId: string): Article[] => {
  return articles.filter((article) => article.categoryId === categoryId);
};

export const getArticleById = (id: string): Article | undefined => {
  return articles.find((article) => article.id === id);
};

export const getArticleBySlug = (slug: string): Article | undefined => {
  return articles.find((article) => article.slug === slug);
};